import type { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AuthService } from 'app/core/auth/auth.service';
import { environment } from 'environments/environment';
import type { Observable } from 'rxjs';
import { BehaviorSubject, catchError, filter, map, switchMap, take, tap, throwError } from 'rxjs';

/**
 * Token Refresh Interceptor
 *
 * Handles automatic token refresh when API returns 401 (Unauthorized) response.
 * This ensures users remain authenticated during long-running API testing sessions
 * and prevents token expiration from interrupting workflows.
 *
 * How it works:
 * 1. When an API request fails with 401 status, the interceptor attempts to refresh the token
 * 2. Multiple concurrent requests share the same refresh operation (via BehaviorSubject)
 * 3. Once refreshed, failed requests are retried with the new token
 * 4. If refresh fails, the user is logged out and redirected to auth-fail page
 *
 * @example
 * This interceptor is registered in app.config.ts:
 * const interceptors = [authInterceptor, tokenRefreshInterceptor, spriLoadingInterceptor];
 *
 * @see see auth-error.interceptor.ts for error standardization
 */

// ============================================
// MODULE-LEVEL SHARED STATE
// ============================================
// These variables are shared across ALL HTTP requests
// This allows concurrent requests to queue and share the same token refresh operation
// WARNING: These persist for the entire application lifetime
// They are reset on each refresh cycle, not per request
let isRefreshInProgress = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const tokenRefreshInterceptor = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const msalService = inject(MsalService);

  // Skip refresh for public endpoints or auth endpoints
  if (isPublicUrl(req.url)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: unknown) => {
      const statusCode = getErrorStatusCode(error);
      // Only handle 401 Unauthorized errors
      // Skip if already an auth endpoint (to avoid infinite loops)
      if (statusCode === 401 && !req.url.includes('/api/auth') && !req.url.includes('/login')) {
        return handleTokenRefresh(req, next, authService, msalService);
      }

      return throwError(() => error);
    })
  );
};

/**
 * Handle token refresh flow with queuing mechanism
 *
 * Multiple concurrent requests are handled efficiently by:
 * - Only one refresh request is made (isRefreshInProgress flag)
 * - Other requests wait for the refresh to complete using BehaviorSubject
 * - All requests are retried with the new token once refresh succeeds
 */
function handleTokenRefresh(
  originalRequest: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  msalService: MsalService
): Observable<HttpEvent<unknown>> {
  if (isRefreshInProgress) {
    // Refresh already in progress - wait for it to complete
    return refreshTokenSubject.pipe(
      // Wait for a non-null token value
      filter((token) => token !== null),
      // Take only the first value
      take(1),
      // Retry the original request with the refreshed token
      switchMap((token) => {
        return next(addTokenToRequest(originalRequest, token));
      }),
      // Handle errors if refresh completed unsuccessfully
      catchError((error) => {
        return throwError(() => error);
      })
    );
  } else {
    // First request to detect token failure - initiate refresh
    isRefreshInProgress = true;
    refreshTokenSubject.next(null);

    return attemptTokenRefresh(msalService, authService).pipe(
      switchMap((refreshedBearerToken: string) => {
        // Refresh succeeded - notify all waiting requests
        isRefreshInProgress = false;
        refreshTokenSubject.next(refreshedBearerToken);

        // Retry the original request with the new token
        return next(addTokenToRequest(originalRequest, refreshedBearerToken));
      }),
      catchError((refreshError) => {
        // Refresh failed - clear refresh state and log user out
        isRefreshInProgress = false;
        refreshTokenSubject.next(null);

        console.error('[TokenRefresh] Token refresh failed:', refreshError);

        // Clear tokens and redirect to login
        sessionStorage.setItem('auth_error', '1');
        authService.signOut(false).subscribe();

        try {
          globalThis.location.replace('/auth-fail');
        } catch {
          location.assign('/auth-fail');
        }

        return throwError(() => refreshError);
      })
    );
  }
}

/**
 * Attempt to refresh the access token using MSAL's acquireTokenSilent
 *
 * MSAL handles the refresh token grant flow automatically:
 * - Exchanges the refresh token for a new access token
 * - Caches the new token locally
 * - Returns the new access token without user interaction
 *
 * @returns Observable<string> - Preferred refreshed bearer token (idToken if available, otherwise accessToken)
 * @throws Error if token refresh fails (user session expired, network error, etc.)
 */
function attemptTokenRefresh(msalService: MsalService, authService: AuthService): Observable<string> {
  // Define the scopes for token refresh
  // These should match the scopes used during login
  const scopes =
    environment.api.scopes && environment.api.scopes.length > 0 ? environment.api.scopes : environment.basicScopes;

  // Use tap and map operators to handle the MSAL Observable
  // This follows RxJS best practices: avoid manual subscriptions in interceptors
  return msalService
    .acquireTokenSilent({
      scopes,
      forceRefresh: false, // Only force refresh if absolutely necessary
    })
    .pipe(
      // Side effect: store the new token in auth service
      tap((result) => {
        if (result.idToken) {
          authService.idToken = result.idToken;
        }
        authService.accessToken = result.accessToken;
        console.debug('[TokenRefresh] Token refresh successful, new token stored');
      }),
      // Prefer refreshed id token for API compatibility, fallback to access token.
      map((result) => result.idToken || result.accessToken),
      // Error handling: log and re-throw the error
      catchError((err) => {
        // Refresh failed scenarios:
        // - Refresh token expired (requires re-authentication)
        // - Network error (no internet connection)
        // - User cancelled interactive prompt (shouldn't happen with silent)
        console.error('[TokenRefresh] acquireTokenSilent failed:', err);

        // Re-throw the error to propagate it upstream to the interceptor
        return throwError(() => err);
      })
    );
}

/**
 * Add the Bearer token to the Authorization header of a request
 */
function addTokenToRequest(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({
    headers: request.headers.set('Authorization', `Bearer ${token}`),
  });
}

/**
 * Check if a URL is public and should skip authentication
 *
 * Public URLs don't require authentication and should not trigger
 * token refresh attempts
 */
function isPublicUrl(url: string): boolean {
  const publicPaths = ['/health', '/swagger', '/api-docs', '/auth/login', '/auth/register', '/public', '/assets'];

  return publicPaths.some((path) => url.includes(path));
}

function getErrorStatusCode(error: unknown): number | null {
  if (error instanceof HttpErrorResponse) {
    return error.status;
  }
  if (typeof error !== 'object' || error === null) {
    return null;
  }
  const maybeStatus = (error as { status?: unknown }).status;
  if (typeof maybeStatus === 'number') {
    return maybeStatus;
  }
  const maybeStatusCode = (error as { statusCode?: unknown }).statusCode;
  if (typeof maybeStatusCode === 'number') {
    return maybeStatusCode;
  }
  return null;
}
